import { Link } from 'react-router-dom'
import { ArrowRight, Shield, Zap, Heart } from 'lucide-react'
import Button from '../components/ui/Button'
import SEO from '../components/SEO'

const BREADCRUMB = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://myschoolportal.xyz' },
    { '@type': 'ListItem', position: 2, name: 'About', item: 'https://myschoolportal.xyz/about' },
  ],
}

const STATS = [
  { value: '13+', label: 'Schools' },
  { value: '5,000+', label: 'Students managed' },
  { value: '20+', label: 'Hours saved weekly' },
  { value: '98%', label: 'Satisfaction' },
]

const VALUES = [
  { icon: Shield, title: 'Security first', desc: 'Role-based access, activity logs and secure authentication for every account.' },
  { icon: Zap, title: 'Fast and reliable', desc: 'Quick response times and high uptime, so your data is there when you need it.' },
  { icon: Heart, title: 'Made for Africa', desc: 'Designed for African schools, from feeding programs to SMS parent alerts.' },
]

export default function About() {
  return (
    <div className="pb-16">
      <SEO
        title="About Tvantra - Built for African Schools"
        description="Built by Tvantra around how Ghanaian schools actually run: GHS pricing, term-based billing, feeding programmes and SMS to parents."
        path="/about"
        schema={[BREADCRUMB]}
      />

      {/* Hero */}
      <section className="px-4 pt-16 pb-10 text-center bg-grid-pattern">
        <div className="max-w-2xl mx-auto">
          <h1 className="text-4xl sm:text-5xl font-display font-extrabold text-ink leading-[1.1] mb-5">
            Built for schools.
          </h1>
          <p className="text-lg text-[var(--ink-soft)] leading-relaxed">
            MySchoolPortal was built to solve real problems: administrators spending hours on spreadsheets, parents waiting for payment information, and teachers needing better access to student data.
          </p>
        </div>
      </section>

      {/* Photo collage */}
      <section className="px-4">
        <div className="max-w-4xl mx-auto">
          <div className="relative h-[220px] sm:h-[300px] max-w-3xl">
            <div className="absolute top-0 left-0 w-[66%] h-full rounded overflow-hidden border border-theme shadow-card z-[1]">
              <img
                src="https://images.unsplash.com/photo-1744809482817-9a9d4fc280af?auto=format&fit=crop&w=1000&q=80"
                alt="A teacher instructing students in a classroom"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="absolute top-[15%] right-0 w-[38%] h-[72%] rounded overflow-hidden shadow-card z-[2]" style={{ border: '4px solid var(--bg-base)' }}>
              <img
                src="https://images.unsplash.com/photo-1744809495173-217ca4faa8bc?auto=format&fit=crop&w=700&q=80"
                alt="A student writing in her notebook"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Mission + stats */}
      <section className="px-4 py-16">
        <div className="max-w-4xl mx-auto grid md:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="text-2xl sm:text-3xl font-display font-bold text-ink mb-4">Software that fits how schools actually run.</h2>
            <p className="text-sm text-[var(--ink-soft)] leading-relaxed mb-3.5">
              We want every school in Ghana and across Africa to have access to management software built around how they operate, not adapted from somewhere else.
            </p>
            <p className="text-sm text-[var(--ink-soft)] leading-relaxed">
              That means SMS for parent notifications, pricing in GHS, and workflows built around school terms, feeding programs and attendance tracking.
            </p>
          </div>

          <div className="rounded p-7" style={{ background: 'linear-gradient(135deg, color-mix(in srgb, var(--accent) 6%, var(--bg-surface)) 0%, var(--bg-surface) 70%)' }}>
            <div className="h-px mb-6" style={{ backgroundImage: 'linear-gradient(to right, var(--rule), transparent 85%)' }} />
            <div className="grid grid-cols-2 gap-5">
              {STATS.map((s) => (
                <div key={s.label}>
                  <div className="font-display font-extrabold text-2xl text-ink">{s.value}</div>
                  <div className="text-xs text-[var(--ink-soft)] mt-1.5">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="px-4 py-20" style={{ background: 'var(--panel-dark)' }}>
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-display font-bold text-white text-center mb-12">What we stand for</h2>
          <div className="grid sm:grid-cols-3 gap-px" style={{ background: 'rgba(255,255,255,0.08)' }}>
            {VALUES.map(({ icon: Icon, title, desc }) => (
              <div key={title} className="p-6" style={{ background: 'var(--panel-dark-raised)' }}>
                <Icon size={22} className="mb-3" style={{ color: 'var(--accent-strong)' }} />
                <h3 className="font-bold text-white mb-1.5">{title}</h3>
                <p className="text-sm leading-relaxed" style={{ color: 'var(--panel-dark-soft)' }}>{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="px-4 py-20 text-center">
        <h2 className="text-2xl sm:text-3xl font-display font-bold text-ink mb-4">Want to work with us?</h2>
        <p className="text-[var(--ink-soft)] mb-8">We're building MySchoolPortal to help schools manage their operations better.</p>
        <div className="flex flex-wrap gap-3 justify-center">
          <Link to="/contact"><Button variant="primary" size="lg">Get in touch <ArrowRight size={16} /></Button></Link>
          <Link to="/contact"><Button variant="ghost" size="lg">Talk to us</Button></Link>
        </div>
      </section>
    </div>
  )
}
